import { Fragment, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Dialog, Transition } from "@headlessui/react";
import {
  setShowKeyboard,
  setKeyboardConfiguration,
  fetchEventBooking,
  resetEventBooking,
} from "../../redux/slices";
import { Spinner } from "../UI/Spinner";
import { useTranslation } from "react-i18next";
import { useKeyboard } from "../../context/KeyboardContext";
import { STATUS } from "../../utils/constants";

const normalise = (value) => String(value ?? "").trim().toLowerCase();

const EventBookingModal = ({ openStatus, closeFunc, setEventBookingData }) => {
  const dispatch = useDispatch();
  const { error, status } = useSelector((state) => state.eventBooking);
  const { orderTotalAmount, orderItems } = useSelector((state) => state.orders);
  const [bookingId, setBookingId] = useState("");
  const [inputError, setInputError] = useState("");
  const { t } = useTranslation(["Common", "OrderPayment"]);
  const { setSetSharedInput } = useKeyboard();
  const isPending = status === STATUS.PENDING;

  // Clear any error left over from a previous attempt each time the modal opens.
  useEffect(() => {
    if (openStatus) {
      dispatch(resetEventBooking());
      setInputError("");
    }
  }, [openStatus, dispatch]);

  // Booking IDs are numeric; strip anything else (e.g. pasted spaces).
  const onBookingIdChange = (value) => {
    setBookingId(String(value ?? "").replace(/\D/g, ""));
  };

  const handleClose = () => {
    setBookingId("");
    setInputError("");
    dispatch(resetEventBooking());
    closeFunc();
  };

  const onSubmit = (e) => {
    e.preventDefault();
    if (isPending) return;

    const trimmedId = bookingId.trim();
    if (!trimmedId) {
      setInputError(t("OrderPayment:paymentchannel_modal.errors.event_required"));
      return;
    }
    setInputError("");

    dispatch(fetchEventBooking(trimmedId))
      .unwrap()
      .then((data) => {
        // A booking is tied to one film, so every ticket in the cart must be
        // for that film before the booking can be applied.
        const bookingFilm = data?.filmName || "";
        const cartFilms = [
          ...new Set(
            (orderItems || [])
              .filter((item) => item.showtimeId)
              .map((item) => item.name)
          ),
        ];
        if (cartFilms.length === 0) {
          setInputError(
            t("OrderPayment:paymentchannel_modal.errors.event_no_ticket", {
              filmName: bookingFilm,
            })
          );
          return;
        }
        const mismatchedFilm = cartFilms.find(
          (film) => !bookingFilm || normalise(film) !== normalise(bookingFilm)
        );
        if (mismatchedFilm !== undefined) {
          setInputError(
            t("OrderPayment:paymentchannel_modal.errors.event_film_mismatch", {
              bookingFilm,
              cartFilm: mismatchedFilm,
            })
          );
          return;
        }

        const bookingAmount = +data?.amount || 0;
        // The booking amount has already been paid; it covers the order up to
        // the order total and any remainder is paid through other channels.
        setEventBookingData({
          bookingId: data?.bookingId || trimmedId,
          amount: Math.min(bookingAmount, orderTotalAmount),
          bookingAmount,
          filmName: bookingFilm,
          totalQuantity: +data?.totalQuantity || 0,
        });
        handleClose();
      })
      .catch(() => {
        // Error is surfaced from the slice state.
      });
  };

  return (
    <Transition appear show={openStatus} as={Fragment}>
      <Dialog as="div" className="relative z-10" onClose={() => {}}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-25" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                <form onSubmit={onSubmit}>
                  {(inputError || error) && (
                    <p className="text-red-500 text-center">
                      {inputError || error}
                    </p>
                  )}
                  <div className="mt-2 deleteModal flex justify-center">
                    <div>
                      <label className="text-center block">
                        {t("OrderPayment:paymentchannel_modal.label.event_cd")}
                      </label>
                      <br />
                      <input
                        type="text"
                        inputMode="numeric"
                        pattern="[0-9]*"
                        value={bookingId}
                        placeholder={t(
                          "OrderPayment:paymentchannel_modal.placeholder.event"
                        )}
                        className=" w-[100%]  h-[50px] border border-secondary rounded py-2 px-3"
                        onChange={(e) => onBookingIdChange(e.target.value)}
                        onClick={() => {
                          dispatch(setShowKeyboard(true));
                          dispatch(
                            setKeyboardConfiguration({
                              type: "numeric",
                              sharedInput: bookingId,
                              placeHolder: t(
                                "OrderPayment:paymentchannel_modal.placeholder.event"
                              ),
                            })
                          );
                          setSetSharedInput(() => onBookingIdChange);
                        }}
                      />
                    </div>
                  </div>

                  <div className="mt-5 flex justify-center">
                    <button
                      type="submit"
                      disabled={isPending}
                      className=" mr-2 inline-flex items-center justify-center rounded-md border border-transparent bg-blue-100 px-4 py-2 text-sm font-medium text-blue-900 hover:bg-blue-200 disabled:cursor-not-allowed disabled:opacity-60 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2"
                    >
                      <span>{t("Common:button.submit")}</span>
                      <Spinner
                        className="!w-3 !h-3 mx-1 text-white fill-blue-800"
                        status={status}
                      />
                    </button>
                    <button
                      type="button"
                      onClick={handleClose}
                      className="ml-2 inline-flex justify-center rounded-md border border-transparent bg-red-700 px-4 py-2 text-sm font-medium text-white mr-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2"
                    >
                      {t("Common:button.cancel")}
                    </button>
                  </div>
                </form>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};
export default EventBookingModal;
